import { compareGlCodes } from './gl-code-sort';

import type { BankAccount } from '@/types/models';

/**
 * Bank sub-account roll-up (#353). A bank account whose GL code is a sub-account
 * of another (via `gl_codes.parent_id`, surfaced on the read as `glParentId`)
 * nests under its parent, and the parent shows a roll-up balance (own + Σ
 * children). Display-only — the register/reconciliation/balance math is
 * untouched. One level of nesting only.
 *
 * This module is intentionally RN-free so it can be unit-tested in plain Vitest.
 */

export interface BankGroupNode {
	/** Parent glCodeId (or a child's glParentId for a synthetic header), or the account id for a standalone node. */
	key: string;
	/** The bank tied to the parent GL code, if one exists. */
	parentAccount: BankAccount | null;
	/** Set only for a synthetic header (children with no parent bank). */
	headerCode?: string;
	/** Set only for a synthetic header. */
	headerName?: string;
	/** Sub-account banks, sorted by glCode via compareGlCodes. */
	children: BankAccount[];
	/** (parentAccount?.currentBalanceCents ?? 0) + Σ children.currentBalanceCents. */
	rollupCents: number;
}

/**
 * Group bank accounts so sub-accounts nest under their GL parent. A "child" is
 * any account with a non-null `glParentId`; children are grouped by that id. A
 * group's parent is the account whose `glCodeId` equals the group key, or a
 * synthetic header (headerCode/headerName from any child) when no such account
 * exists. Accounts that are neither a child nor a group parent are emitted as
 * standalone single-account nodes. Top-level nodes and each group's children are
 * ordered with `compareGlCodes`.
 */
export function groupBankAccountsByGlParent(accounts: BankAccount[]): BankGroupNode[] {
	const childrenByParent = new Map<string, BankAccount[]>();
	for (const a of accounts) {
		if (a.glParentId) {
			const list = childrenByParent.get(a.glParentId) ?? [];
			list.push(a);
			childrenByParent.set(a.glParentId, list);
		}
	}

	// An account is a "group parent" when some child points at its GL code.
	const isGroupParent = (a: BankAccount): boolean => !!a.glCodeId && childrenByParent.has(a.glCodeId);
	const parentAccountByGlId = new Map<string, BankAccount>();
	for (const a of accounts) {
		if (a.glCodeId && childrenByParent.has(a.glCodeId)) parentAccountByGlId.set(a.glCodeId, a);
	}

	const nodes: BankGroupNode[] = [];
	const emittedAsChild = new Set<string>();
	const emittedAsParent = new Set<string>();

	// Grouped nodes: one per parent glId that has children. We only nest a LEAF
	// child here — a child that is itself a group parent (a middle node in a 3+
	// level GL chain) is hoisted to its own top-level group instead, so no
	// account ever renders twice (display is one level deep).
	for (const [key, kids] of childrenByParent) {
		const children = kids
			.filter((k) => !isGroupParent(k))
			.sort((x, y) => compareGlCodes(x.glCode ?? '', y.glCode ?? ''));
		if (children.length === 0) continue; // its only children were hoisted; parent falls to standalone
		const parentAccount = parentAccountByGlId.get(key) ?? null;
		const sample = children[0];
		const childSum = children.reduce((s, c) => s + c.currentBalanceCents, 0);
		for (const c of children) emittedAsChild.add(c.id);
		if (parentAccount) emittedAsParent.add(parentAccount.id);
		nodes.push({
			key,
			parentAccount,
			headerCode: parentAccount ? undefined : (sample?.glParentCode ?? undefined),
			headerName: parentAccount ? undefined : (sample?.glParentName ?? undefined),
			children,
			rollupCents: (parentAccount?.currentBalanceCents ?? 0) + childSum,
		});
	}

	// Standalone nodes: anything not already rendered as a nested child or a
	// group parent (includes hoisted middle nodes whose own group had no leaves).
	for (const a of accounts) {
		if (emittedAsChild.has(a.id) || emittedAsParent.has(a.id)) continue;
		nodes.push({ key: a.id, parentAccount: a, children: [], rollupCents: a.currentBalanceCents });
	}

	const codeOf = (n: BankGroupNode): string => n.parentAccount?.glCode ?? n.headerCode ?? '';
	const nameOf = (n: BankGroupNode): string => n.parentAccount?.name ?? n.headerName ?? '';
	nodes.sort((x, y) => {
		const cmp = compareGlCodes(codeOf(x), codeOf(y));
		return cmp !== 0 ? cmp : nameOf(x).localeCompare(nameOf(y));
	});

	return nodes;
}
